import React from 'react'
import _ from 'underscore'
import moment from 'moment'
import { Bell, ChevronDown, CircleAlert, ExternalLink, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DropdownMenu } from '../dropdown_menu'
import TaskAssignee from './assignee'
import TaskDue from './due'
import DebouncedTextarea from '../debounced_textarea'
import Textarea from '../textarea'

class TasksPartial extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      hovering: false
    }

    this.onDelete = _.once(this.onDelete).bind(this)
  }

  componentWillUnmount() {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer)
    }
  }

  onToggle = () => {
    if (this.props.task.id) {
      this.props.onToggle(this.props.task)
    }
  }

  onDelete = () => {
    this.props.onDelete(this.props.task)
  }

  onUpdate = (params = {}) => {
    const updatePArams = _.extend({
      name: this.props.task.name,
      due_at: this.props.task.due_at,
      reminder: this.props.task.reminder,
      priority: this.props.task.priority,
      owner_id: this.props.task.owner_id,
      owner: this.props.task.owner,
      team: this.props.task.team
    }, params)

    this.props.onUpdate(this.props.task, updatePArams)
  }

  onLinkClick = (e) => {
    if (this.props.onLinkClick) {
      this.props.onLinkClick(this.props.task.data.url)
    }
  }

  onNameChange = (name) => {
    if ((this.props.task.id) && (name === '')) {
      return
    }

    let due_at = this.props.task.due_at

    if (!due_at) {
      due_at = moment().format("YYYY-MM-DD HH:mm")
    }

    this.onUpdate({name: name, due_at: due_at})
  }

  onDueAtChange = (value) => {
    this.onUpdate({due_at: value})
  }

  onOwnerChange = (assignee) => {
    if (assignee.type === 'user') {
      this.onUpdate({owner_id: assignee.id, owner: assignee})
    }
    else if (assignee.type === 'team') {
      this.onUpdate({owner_id: null, owner: null, team: assignee.id})
    }
  }

  onReminderClick = () => {
    this.onUpdate({reminder: !this.props.task.reminder})
  }

  onPriorityClick = () => {
    this.onUpdate({priority: !this.props.task.priority})
  }

  onMouseEnter = () => {
    this.hoverTimer = window.setTimeout(() => { this.setState({hovering: true}) }, 500)
  }

  onMouseLeave = () => {
    this.setState({hovering: false})
    clearTimeout(this.hoverTimer)
  }

  onNameEnterKey = (e) => {
    e.preventDefault()

    if (!this.props.task.id) {
      this.props.onCreate()
    }
  }

  onCommonTaskClick = (name) => {
    this.onNameChange(name)
  }

  render() {
    const task = this.props.task
    const owner = task.owner
    const team = task.team
    const hasName = (task.name || '').length > 0

    return (
      <div
        className="group/task flex flex-col gap-2 py-1 [&_textarea]:bg-transparent [&_textarea]:border-0! [&_textarea]:shadow-none [&_textarea]:resize-none [&_textarea]:overflow-visible [&_textarea]:px-1.5 [&_textarea]:py-1 [&_textarea]:min-h-0 [&_textarea]:rounded-sm [&_textarea]:text-sm"
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <div className="flex items-center gap-2">
          <div className="shrink-0">
            {task.actions.can_edit ?
              <Checkbox
                checked={task.workflow_state === 'complete'}
                onCheckedChange={this.onToggle}
              />
            :
              <Settings className="size-4 text-muted-foreground"/>
            }
          </div>
          <div className="min-w-0 flex-1">
            {task.id ?
              <DebouncedTextarea
                value={task.name || ''}
                placeholder="Add a task"
                rows='1'
                expands='on-change'
                onChange={this.onNameChange}
                onEnter={this.onNameEnterKey}
                onClear={this.onDelete}
              />
            :
              <Textarea
                value={task.name || ''}
                placeholder="Add a task"
                rows='1'
                expands='on-change'
                onChange={this.onNameChange}
                onEnter={this.onNameEnterKey}
                onClear={this.onDelete}
              />
            }
          </div>
          {hasName ?
            <div className="flex shrink-0 items-center gap-1 whitespace-nowrap">
              <Button
                variant="ghost"
                size="icon-xs"
                title={task.reminder ? 'Reminder on — click to disable' : 'Set reminder'}
                onClick={this.onReminderClick}
                className={task.reminder ? 'text-warning hover:text-warning' : 'text-muted-foreground hover:text-foreground'}
              >
                <Bell className="size-4" fill={task.reminder ? 'currentColor' : 'none'}/>
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                title={task.priority ? 'Priority on — click to disable' : 'Mark as priority'}
                onClick={this.onPriorityClick}
                className={task.priority ? 'text-destructive hover:text-destructive' : 'text-muted-foreground hover:text-foreground'}
              >
                <CircleAlert className="size-4"/>
              </Button>
              {(task.data || {}).url ?
                <Button
                  variant="ghost"
                  size="icon-xs"
                  title="Open link"
                  onClick={this.onLinkClick}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="size-4"/>
                </Button>
                : null
              }
              <TaskDue
                key={task.due_at}
                due_at={task.due_at}
                onChange={this.onDueAtChange}
              />
              {(owner || team || this.props.assignees.length > 0) ?
                <TaskAssignee
                  assignee={owner}
                  team={team}
                  assignees={this.props.assignees}
                  onChange={this.onOwnerChange}
                />
                : null
              }
            </div>
          :
            <div className="shrink-0">
              {((this.props.common_tasks || []).length > 0) ?
                <DropdownMenu
                  buttonClassName="btn btn-outline"
                  className="dropdown-menu dropdown-menu-right"
                  items={_.map(this.props.common_tasks, (common_task) => ({value: common_task.name, name: common_task.name}))}
                  onItemClick={this.onCommonTaskClick}
                >
                  <ChevronDown className="size-3.5"/>
                </DropdownMenu>
                : null
              }
            </div>
          }
        </div>
        {(!task.id && hasName) ?
          <div className="flex justify-end gap-2 pl-6">
            <Button size="sm" variant="ghost" type="button" onClick={this.props.onCancel}>Cancel</Button>
            <Button size="sm" type="button" onClick={this.props.onCreate}>Add</Button>
          </div>
          : null
        }
      </div>
    )
  }
}

export default TasksPartial
